import { Form, Formik, FormikErrors } from "formik";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem
} from "@material-ui/core";

import {
  CONFIRMATION_TEXT,
  ApprovalFields
} from "src/components/ApprovalFields";
import { useAdmin, useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import { subaccountCreateVariables } from "src/graphql/generated/subaccountCreate";
import { areaCodeHelperText } from "src/util/areaCodeHelperText";
import { getErrorMessage } from "src/util/errors";

type subaccountCreateInput = Omit<
  subaccountCreateVariables,
  "confirmationTextAtTimeOfUpdate"
>;

type FormValuesAndErrors = subaccountCreateInput & {
  saveErrors?: any;
};

export default function AddSubaccountDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: subaccountCreateVariables) => Promise<void>;
}): JSX.Element {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);
  const tenant = useTenant();

  const handleSubmit = async (payload: subaccountCreateInput) => {
    setSaveErrors(null);

    try {
      await onSubmit({
        ...payload,
        confirmationTextAtTimeOfUpdate: CONFIRMATION_TEXT
      });
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>New Subaccount</DialogTitle>
      <Formik
        initialValues={{
          name: "",
          defaultAreaCode: "",
          defaultTimezone: "",
          customer_profile_sid: "",
          approved_stir_shaken_sid: "",
          approved_voice_integrity_sid: "",
          approval_requirement: tenant!.approval_requirement,
          voicemail_allowed: tenant!.voicemail_allowed
        }}
        validationSchema={yup.object().shape({
          name: yup.string().required("Required"),
          defaultTimezone: yup.string().required("Required"),
          defaultAreaCode: yup
            .string()
            .required("Required")
            .areaCode(tenant?.countryIso),
          customer_profile_sid: yup.string(),
          approved_stir_shaken_sid: yup.string(),
          approved_voice_integrity_sid: yup.string(),
          approval_requirement: yup.string().required(),
          voicemail_allowed: yup.boolean().required()
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <AddSubaccountForm
            onClose={onClose}
            isSubmitting={isSubmitting}
            saveErrors={saveErrors}
          />
        )}
      </Formik>
    </Dialog>
  );
}

function AddSubaccountForm({
  isSubmitting,
  onClose,
  saveErrors
}: {
  isSubmitting?: boolean;
  onClose: () => void;
  saveErrors: string | null;
}) {
  const tenant = useTenant()!;
  const admin = useAdmin()!;

  return (
    <Form>
      <DialogContent>
        <FormikErrorBanner
          renderBanner={(errors: FormikErrors<FormValuesAndErrors>) => {
            if (errors.saveErrors) {
              return `Error saving subaccount: ${errors.saveErrors}`;
            }
            return "Please fix the errors indicated below.";
          }}
          additionalErrors={saveErrors}
        />
        <FormikTextField fullWidth name="name" label="Subaccount name" />
        <FormikSelect
          name="defaultTimezone"
          fullWidth
          label="Default time zone"
          disabled={isSubmitting}
        >
          {tenant.timezoneOptions.map(({ displayName, timezone }) => (
            <MenuItem key={timezone} value={timezone}>
              {displayName}
            </MenuItem>
          ))}
        </FormikSelect>
        <FormikTextField
          label="Default area code"
          name="defaultAreaCode"
          helperText={areaCodeHelperText(tenant.countryIso)}
          disabled={isSubmitting}
          fullWidth
        />
        {admin.isStaff && (
          <>
            <FormikTextField
              name="customer_profile_sid"
              label="Twilio Customer Profile SID"
              fullWidth
            />
            <FormikTextField
              name="approved_stir_shaken_sid"
              label="Approved Trust Product SID for STIR/SHAKEN"
              fullWidth
            />
            <FormikTextField
              name="approved_voice_integrity_sid"
              label="Approved Trust Product SID for Voice Integrity Beta"
              fullWidth
            />
          </>
        )}
        <ApprovalFields />
      </DialogContent>

      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
}
